import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { TabsList } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import {
  getDashboardRoute,
  getProjectAccessRoute,
  getProjectActivitiesRoute,
  getProjectDetailsByUuid,
  getProjectDetailsRoute,
  getProjectDocumentsRoute,
  getProjectInventoryRoute,
  getProjectLocationRoute,
  getProjectMarketingSettingsRoute,
  getProjectTasksRoute,
} from "../../../../utils/routes";
import { ProjectContext } from "../../../shared/projects/project/ProjectContext";

interface UseProjectReturnData {
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
}

export const useProject = (): UseProjectReturnData => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectDetails, publicProjectDetails } = useContext(ProjectContext);
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const [persistedStateOrigin, setPersistedStateOrigin] = useState<IStateOrigin | undefined>(stateOrigin);
  const origin = searchParams.get("origin") ?? undefined;
  const effectiveStateOrigin = stateOrigin ?? persistedStateOrigin;

  useEffect(() => {
    if (stateOrigin) {
      setPersistedStateOrigin(stateOrigin);
    }
  }, [stateOrigin]);

  const backButtonText = effectiveStateOrigin?.goBackText ?? "Back to dashboard";

  const handleBackClick = (): void => {
    if (effectiveStateOrigin?.from) {
      navigate(effectiveStateOrigin.from);
      return;
    }

    navigate(getDashboardRoute(OrganisationTypeConstants.ASSET_MANAGER));
  };

  const getTabsList = (): TabsList => {
    let list: TabsList = [];
    if (projectDetails) {
      list = [
        {
          label: "Overview",
          route: getProjectDetailsByUuid(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        {
          label: "Details",
          route: getProjectDetailsRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        {
          label: "Location",
          route: getProjectLocationRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        // REQ-014: Inventory tab is hidden when the project has no unit measurements (null or empty, e.g. No standard projects).
        ...(projectDetails?.unitMeasurements?.length
          ? [
              {
                label: "Inventory",
                route: getProjectInventoryRoute(
                  projectDetails?.uuid || "",
                  OrganisationTypeConstants.ASSET_MANAGER,
                  origin
                    ? {
                        origin,
                      }
                    : undefined
                ),
              },
            ]
          : []),
        {
          label: "Activities",
          route: getProjectActivitiesRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        {
          label: "Documents",
          route: getProjectDocumentsRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        {
          label: "Marketing",
          route: getProjectMarketingSettingsRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin ? `origin=${origin}` : undefined
          ),
        },
        {
          label: "Access",
          route: getProjectAccessRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        {
          label: "Tasks",
          route: getProjectTasksRoute(
            projectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
      ];
    }
    if (publicProjectDetails && !projectDetails) {
      list = [
        {
          label: "Overview",
          route: getProjectDetailsByUuid(
            publicProjectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
        // REQ-014: Inventory tab is hidden when the project has no unit measurements (null or empty, e.g. No standard projects).
        ...(publicProjectDetails?.unitMeasurements?.length
          ? [
              {
                label: "Inventory",
                route: getProjectInventoryRoute(
                  publicProjectDetails?.uuid || "",
                  OrganisationTypeConstants.ASSET_MANAGER,
                  origin
                    ? {
                        origin,
                      }
                    : undefined
                ),
              },
            ]
          : []),
        {
          label: "Documents",
          route: getProjectDocumentsRoute(
            publicProjectDetails?.uuid || "",
            OrganisationTypeConstants.ASSET_MANAGER,
            origin
              ? {
                  origin,
                }
              : undefined
          ),
        },
      ];
    }

    return list;
  };

  return {
    handleBackClick,
    tabsList: getTabsList(),
    backButtonText,
  };
};
